import { exec, getApiToken } from '../utils';
import type { CatalogRule } from '@src/components/catalog/CatalogRules/types';
import { CatalogRuleInput, toRuleInputs } from './saveCatalogRules';

interface PreviewCatalogRulesArgs {
  partnerCode: string;
  token: string;
  rules: CatalogRuleInput[];
}

interface PreviewCatalogRuleRawResult {
  position: number;
  itemsMatched: number;
}

interface PreviewCatalogRulesRawResult {
  totalItemsMatched: number;
  ruleResults: PreviewCatalogRuleRawResult[];
}

export interface PreviewCatalogRulesResult {
  totalItemsMatched: number;
  ruleMatches: number[];
}

interface PreviewCatalogRulesExecResponse {
  restUser: {
    partner: {
      previewCatalogRules: PreviewCatalogRulesRawResult;
    };
  };
}

/**
 * Previews catalog rules for a partner without applying them.
 * Returns the number of catalog records that would be affected.
 * @param partnerCode - The partner code to preview rules for
 * @param rules - The complete array of rule groups to preview
 * @returns Promise resolving to preview result with match counts
 */
export const previewCatalogRules = async (
  partnerCode: string,
  rules: CatalogRule[],
): Promise<PreviewCatalogRulesResult> => {
  const apiToken = await getApiToken();

  const query = `
    query previewCatalogRules(
      $partnerCode: String!
      $token: String!
      $rules: [CatalogRuleInputRestUser!]!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          previewCatalogRules(rules: $rules) {
            totalItemsMatched
            ruleResults {
              position
              itemsMatched
            }
          }
        }
      }
    }
  `;

  const data = await exec<PreviewCatalogRulesArgs, PreviewCatalogRulesExecResponse>(
    query,
    { variables: { token: apiToken, partnerCode, rules: toRuleInputs(rules) } },
  );

  const raw = data.restUser.partner.previewCatalogRules;
  const ruleMatches = raw.ruleResults
    .sort((a, b) => a.position - b.position)
    .map(r => r.itemsMatched);

  return {
    totalItemsMatched: raw.totalItemsMatched,
    ruleMatches,
  };
};
